import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { PRENSA } from "@/data/prensa";

/* -------------------------------------------------------------
   Prensa — cobertura de medios de Arteconciencia.
   Las notas se agregan en /src/data/prensa.js (medio, titular,
   fecha, imagen, descripción y enlace externo).
   ------------------------------------------------------------- */
export default function Prensa() {
  const { lang } = useLang();
  const isEn = lang === "en";

  useSEO({
    title: "Prensa | Arteconciencia",
    description:
      "Consulta entrevistas, artículos y cobertura de medios sobre Arteconciencia, LATIR y sus proyectos de arte y concientización en El Salvador.",
    canonical: "/prensa",
  });

  const formatFecha = (iso) => {
    try {
      return new Date(`${iso}T12:00:00`).toLocaleDateString(isEn ? "en-US" : "es-SV", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <main className="bg-paper min-h-screen" data-testid="prensa-page">
      <section className="pt-28 sm:pt-32 md:pt-36 pb-14 md:pb-20">
        <div className="mx-auto max-w-[1180px] px-5 md:px-10">
          <span className="eyebrow text-ink-mute inline-flex items-center gap-3 mb-6">
            <span className="w-6 h-px bg-latir-red inline-block" />
            {isEn ? "Press" : "Prensa"}
          </span>
          <h1
            className="font-display text-[2.1rem] sm:text-[3rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em] text-ink max-w-[22ch]"
            data-testid="prensa-title"
          >
            {isEn ? "Arteconciencia in the media" : "Arteconciencia en los medios"}
          </h1>
          <p className="mt-6 font-editorial text-[1.05rem] sm:text-[1.25rem] leading-[1.5] text-ink-soft max-w-[52ch]">
            {isEn
              ? "Interviews, articles and media coverage about Arteconciencia, LATIR — El placer de vivir and its art and awareness projects in El Salvador."
              : "Entrevistas, artículos y cobertura de medios sobre Arteconciencia, LATIR — El placer de vivir y sus proyectos de arte y concientización en El Salvador."}
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="mx-auto max-w-[1180px] px-5 md:px-10">
          {PRENSA.length === 0 ? (
            <div className="border-t border-line pt-10" data-testid="prensa-empty">
              <p className="font-editorial italic text-[1rem] text-ink-mute max-w-[52ch]">
                {isEn
                  ? "Media coverage will be published here soon."
                  : "La cobertura de medios se publicará aquí próximamente."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
              {PRENSA.map((nota, i) => (
                <article key={`${nota.medio}-${i}`} className="border-t border-line pt-5" data-testid={`prensa-item-${i}`}>
                  {nota.imagen && (
                    <img
                      src={nota.imagen}
                      alt={`${nota.titular} — ${nota.medio}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto object-contain mb-4 bg-paper-deep border border-line"
                    />
                  )}
                  <p className="font-ui uppercase tracking-[0.2em] text-[0.62rem] text-latir-red mb-2">
                    {nota.medio} · {formatFecha(nota.fecha)}
                  </p>
                  <h2 className="font-display text-[1.35rem] sm:text-[1.55rem] leading-[1.15] text-ink mb-3">
                    {nota.titular}
                  </h2>
                  <p className="font-editorial text-[0.95rem] leading-[1.6] text-ink-soft mb-4">{nota.descripcion}</p>
                  <a
                    href={nota.enlace}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${isEn ? "Read the full article at" : "Leer la nota completa en"} ${nota.medio}`}
                    data-testid={`prensa-link-${i}`}
                    className="inline-flex items-center gap-2 font-ui uppercase tracking-[0.18em] text-[0.68rem] text-ink hover:text-latir-red transition-colors"
                  >
                    {isEn ? "Read at" : "Leer en"} {nota.medio} ↗
                  </a>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
